import { toast } from "react-toastify";
import { Inertia } from "@inertiajs/inertia";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { LogIn, MoreVertical, Star } from "react-feather";
import { ActionMenu } from "@/Components/ActionMenu";
import { IconLock } from "@/Icons";
import { showToast } from "@/Utilities/Utilities";
import ClientDialogCenter from "@/Pages/Agency/Clients/Partials/ClientDialogCenter";

export const ClientCard = ({ organization, favoriteIds = [] }) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showInactivateModal, setShowInactivateModal] = useState(false);

  const handleUnlock = useCallback(() => {
    const id = toast.loading("Please wait...");
    Inertia.put(route("agency.clients.unlock", organization.id), null, {
      onSuccess: () => showToast(id, "Your client was successfully restored"),
      onError: () =>
        showToast(
          id,
          "Something went wrong when restoring your client",
          "error",
        ),
    });
  }, [organization.id]);

  const handleFavorite = (event) => {
    event.preventDefault();
    Inertia.put(route("agency.clients.mark-as-favorite", organization.id));
  };

  const actionMenuItems = useMemo(() => {
    if (!organization.locked_at) {
      return [
        {
          label: "Edit client",
          callback: () =>
            Inertia.visit(route("agency.clients.edit", organization.id)),
        },
        {
          label: "Switch to client",
          callback: () =>
            Inertia.put(route("agency.clients.view", organization.id)),
        },
        {
          label: "Inactivate",
          callback: () => setShowInactivateModal(true),
        },
        {
          label: "Delete",
          callback: () => setShowDeleteModal(true),
        },
      ];
    } else {
      return [
        {
          label: "Reactivate",
          callback: () => handleUnlock(),
        },
        {
          label: "Delete",
          callback: () => setShowDeleteModal(true),
        },
      ];
    }
  }, [organization.locked_at, handleUnlock]);

  const actionMenuRef = useRef(null);

  const isFavorite = favoriteIds.includes(organization.id);

  return (
    <>
      <div className="shadow-small flex flex-col items-start gap-8 bg-gray-light px-8 py-6">
        <div className="flex min-h-[100px] items-start self-stretch">
          <div className="flex grow flex-col items-start gap-1">
            <div className="self-stretch hyphens-auto break-all font-overpass text-2xl font-bold">
              {organization.name}
            </div>
          </div>
          <div className="hover-circle relative z-10 ml-2">
            <MoreVertical
              size={24}
              className="shrink-0 cursor-pointer"
              onClick={(e) => {
                e.preventDefault();
                actionMenuRef.current.toggleMenu();
              }}
            />
            <ActionMenu ref={actionMenuRef} items={actionMenuItems} />
          </div>
        </div>
        <div className="flex flex-wrap items-end justify-between gap-2 self-stretch">
          {!organization.locked_at && (
            <>
              <div className="flex shrink-0 items-start gap-4">
                <button
                  className="flex items-end gap-2 text-primary underline hover:no-underline"
                  onClick={() =>
                    Inertia.put(route("agency.clients.view", organization.id))
                  }
                >
                  <LogIn size={24} />
                  Switch to
                </button>
              </div>
              <div className="flex grow justify-end">
                <button className="hover-circle relative">
                  <Star
                    size={24}
                    fill={isFavorite ? "#FFB800" : "transparent"}
                    stroke={isFavorite ? "#FFB800" : "#959BAB"}
                    onClick={handleFavorite}
                  />
                </button>
              </div>
            </>
          )}
          {organization.locked_at && (
            <p className="flex items-center gap-2 text-typo-inactive">
              <IconLock size={21} /> Inactivated {organization.locked_at}
            </p>
          )}
        </div>
      </div>
      <ClientDialogCenter
        organization={organization}
        showDeleteModal={showDeleteModal}
        setShowDeleteModal={setShowDeleteModal}
        showInactivateModal={showInactivateModal}
        setShowInactivateModal={setShowInactivateModal}
      />
    </>
  );
};
